import { isMidnight } from '@fullstory/util/date';
import moment, { Moment } from 'moment';
import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { TextInput } from '../../../../components/TextInput';
import { identifyComponent } from '../../../../util';
import { DateRangePickerContext, TimeDisplayMode } from '../../context';
import { FORMAT_LONG, FORMAT_LONG_DAY, FORMAT_SHORT, PARSE_FORMATS } from './constants';
import { RightAdornment } from './RightAdornment';

interface DateInputProps {
  date: Moment | null;
  onFocus: () => void;
  onFocusNext: () => void;
  onChange: (day: Moment) => void;
  v2?: boolean;
}

export const DateInput = forwardRef<{ blur?: () => void; focus: () => void }, DateInputProps>(
  ({ date, onFocus, onFocusNext, onChange }, ref) => {
    const { timeDisplayMode, readOnly, includeTimeInputs } = useContext(DateRangePickerContext);
    const [touched, setTouched] = useState(false);
    const [focused, setFocused] = useState(false);
    const [inputValue, setInputValue] = useState(
      formatInput(date, { timeDisplayMode, isFocused: true }),
    );
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        blur: () => {
          inputRef.current?.blur();
        },
        focus: () => {
          inputRef.current?.focus();
        },
      }),
      [],
    );

    // select the input text after input value is set if input is focused
    useEffect(() => {
      if (focused) {
        inputRef.current?.select();
      }
    }, [focused]);

    useEffect(() => {
      setInputValue(formatInput(date, { timeDisplayMode, isFocused: true }));
    }, [timeDisplayMode, date]);

    const handleFocus = () => {
      setFocused(true);
      onFocus();
    };

    return (
      <TextInput
        data-component="DateInput"
        ref={inputRef}
        placeholder={moment().localeData().longDateFormat(FORMAT_LONG_DAY)}
        value={focused ? inputValue : formatInput(date, { timeDisplayMode, isFocused: false })}
        onBlur={() => {
          setTouched(false);
          setFocused(false);
          if (touched) {
            // merge time from date with date from input
            const inputMoment = moment(inputValue, PARSE_FORMATS);
            if (date && includeTimeInputs) {
              inputMoment.set({
                hour: date.get('hour'),
                minute: date.get('minute'),
                second: date.get('second'),
                millisecond: date.get('millisecond'),
              });
            }
            onChange(inputMoment);
          }
        }}
        onChange={evt => {
          setTouched(true);
          setInputValue(evt.target.value);
        }}
        onFocus={handleFocus}
        onKeyDown={evt => {
          if (evt.key === 'Enter') {
            const isDateValid = moment(inputValue, PARSE_FORMATS).isValid();
            if (isDateValid) {
              evt.preventDefault();
              evt.stopPropagation();
              onFocusNext();
            }
          }
        }}
        size="small"
        rightAdornment={
          !includeTimeInputs && (
            <RightAdornment focused={focused} date={date} onClick={handleFocus} />
          )
        }
        invalid={date != null ? !date?.isValid() : undefined}
        testId="date-range-picker-date-input"
        readOnly={readOnly}
        {...identifyComponent('date-input')}
        fullWidth
      />
    );
  },
);
DateInput.displayName = 'DateInput';

interface FormatInputOptions {
  timeDisplayMode: TimeDisplayMode;
  isFocused: boolean;
}

const formatInput = (date: Moment | null, { timeDisplayMode, isFocused }: FormatInputOptions) => {
  if (!date) return '';

  switch (timeDisplayMode) {
    case 'never':
      return moment(date).format(FORMAT_SHORT);
    // We never want to show the long format when the input is unfocused because it's displayed in the
    // right adornment
    case 'always':
    case 'onfocus':
      return isFocused ? moment(date).format(FORMAT_LONG) : moment(date).format(FORMAT_SHORT);
    case 'auto': {
      // Auto omits the timestamp if the time of day is midnight and when the input is not focused
      const longFormat = isMidnight(date) ? FORMAT_LONG_DAY : FORMAT_LONG;
      return isFocused ? moment(date).format(longFormat) : moment(date).format(FORMAT_SHORT);
    }
  }
};
